// FLEET LIVE's frame scheduler (#6778): ONE loop for every canvas in the section, so the
// Command Deck and the Topology share a clock and a 240-machine org costs one
// `requestAnimationFrame` chain, not one per panel or per item.
//
// THREE GATES, each an injected reading so a test drives them without a browser:
//   - `tabVisible`: a hidden tab requests no frame (`visibilitychange` calls `wake`);
//   - `onScreen`: a section more than 120 px off-screen requests no frame
//     (`IntersectionObserver` with a 120 px root margin calls `wake`);
//   - `reducedMotion`: REDUCED MOTION IS A STILL FRAME (owner ruling V9 #1). One draw on
//     mount and one per data patch (`invalidate`), with `dt = 0`, and no frame requested
//     between them. The prototype kept animating here (`prototype.html:4309,4316,4331`),
//     which is brief.md item 46.
//
// A draw wanted while a gate is shut is not lost: `dirty` stays set, and the next `wake`
// with every gate open paints it.

/** The longest step a frame may take, so a tab returning from the background does not jump. */
export const MAX_FRAME_DT = 64;

export type FrameDeps = {
  readonly requestFrame: (cb: (now: number) => void) => number;
  readonly cancelFrame: (id: number) => void;
  readonly reducedMotion: () => boolean;
  readonly tabVisible: () => boolean;
  readonly onScreen: () => boolean;
  /** Paint every shown panel. `dt` is 0 on a still frame and on a loop's first frame. */
  readonly draw: (now: number, dt: number) => void;
};

export type FrameScheduler = {
  /** The data (or the size) changed: paint again. */
  readonly invalidate: () => void;
  /** A gate may have opened or shut: resume, or stop requesting. */
  readonly wake: () => void;
  /** The section unmounted: request nothing more. */
  readonly stop: () => void;
};

export function createFrameScheduler(deps: FrameDeps): FrameScheduler {
  let pending: number | null = null;
  let dirty = true;
  let last: number | null = null;
  let stopped = false;

  const open = (): boolean => !stopped && deps.tabVisible() && deps.onScreen();

  const schedule = (): void => {
    if (pending !== null || !open()) return;
    pending = deps.requestFrame(frame);
  };

  function frame(now: number): void {
    pending = null;
    if (!open()) {
      last = null;
      return;
    }
    if (deps.reducedMotion()) {
      last = null;
      if (!dirty) return;
      dirty = false;
      deps.draw(now, 0);
      return;
    }
    const dt = last === null ? 0 : Math.min(MAX_FRAME_DT, now - last);
    last = now;
    dirty = false;
    deps.draw(now, dt);
    schedule();
  }

  return {
    invalidate: () => {
      dirty = true;
      schedule();
    },
    wake: () => {
      if (!open()) {
        if (pending !== null) deps.cancelFrame(pending);
        pending = null;
        last = null;
        return;
      }
      if (deps.reducedMotion() && !dirty) return;
      schedule();
    },
    stop: () => {
      stopped = true;
      if (pending !== null) deps.cancelFrame(pending);
      pending = null;
    },
  };
}
